import { tokenValues } from '@client/design-system/__generated__';
import {
    ADMIN_ROLE_METADATA,
    type AdminRoleGroup,
    type OrgRoleGroup,
    ROLE_METADATA,
} from '@shared/constants/business/role-metadata';
import { ADMIN_ROLES, type AdminRole, ORG_ROLES, type OrgRole, type Role } from '@shared/constants/business/user';
import {
    Bot,
    Building2,
    ClipboardCheck,
    ClipboardList,
    ClipboardPen,
    Crown,
    Eye,
    FileText,
    Globe,
    GraduationCap,
    Headphones,
    Layers,
    MessageSquare,
    Send,
    Server,
    Settings,
    Share2,
    Shield,
    Truck,
    User,
    Users,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { getRoleColor } from './role-colors';

/** Role color values from the design system */
const ROLE_COLORS = tokenValues.color.role;

/** Default grey color for unknown roles */
const DEFAULT_ROLE_COLOR = ROLE_COLORS.grey;

const ROLE_ACRONYMS = new Map<string, string>([
    ['ai', 'AI'],
    ['api', 'API'],
    ['db', 'DB'],
    ['mcp', 'MCP'],
    ['pds', 'PDS'],
]);

export interface RoleConfig {
    /** Ant Design colour name */
    color: string;
    /** Human-readable display name */
    displayName: string;
    /** Icon component factory (pass size to render) */
    icon: (size?: number) => ReactNode;
}

/**
 * Configuration for each role including colour, display name, and icon
 */
export const ROLE_CONFIG: Record<Role, RoleConfig> = {
    super_admin: {
        color: 'red',
        displayName: 'Super admin',
        icon: (size = 12) => <Crown size={size} />,
    },
    // Read-only admin role
    admin_read_only: {
        color: 'grey',
        displayName: 'Admin read only',
        icon: (size = 12) => <Eye size={size} />,
    },
    // Granular admin console roles
    admin_org_manager: {
        color: 'volcano',
        displayName: 'Organisation manager',
        icon: (size = 12) => <Building2 size={size} />,
    },
    admin_user_manager: {
        color: 'gold',
        displayName: 'User manager',
        icon: (size = 12) => <Users size={size} />,
    },
    admin_content_manager: {
        color: 'cyan',
        displayName: 'Content manager',
        icon: (size = 12) => <FileText size={size} />,
    },
    admin_comms_manager: {
        color: 'purple',
        displayName: 'Communications manager',
        icon: (size = 12) => <MessageSquare size={size} />,
    },
    admin_ai_manager: {
        color: 'blue',
        displayName: 'AI manager',
        icon: (size = 12) => <Bot size={size} />,
    },
    admin_system_ops: {
        color: 'red',
        displayName: 'System operations',
        icon: (size = 12) => <Settings size={size} />,
    },
    admin_ops_reader: {
        color: 'orange',
        displayName: 'Ops reader',
        icon: (size = 12) => <Eye size={size} />,
    },
    admin_support_manager: {
        color: 'geekblue',
        displayName: 'Support manager',
        icon: (size = 12) => <Headphones size={size} />,
    },
    admin_support_user: {
        color: 'blue',
        displayName: 'Support user',
        icon: (size = 12) => <Headphones size={size} />,
    },
    // Organisation roles
    org_admin: {
        color: 'purple',
        displayName: 'Organisation admin',
        icon: (size = 12) => <Shield size={size} />,
    },
    user_manager: {
        color: 'blue',
        displayName: 'User manager',
        icon: (size = 12) => <Users size={size} />,
    },
    supplier: {
        color: 'orange',
        displayName: 'Supplier',
        icon: (size = 12) => <Building2 size={size} />,
    },
    auto_detect_user: {
        color: 'cyan',
        displayName: 'Auto detect user',
        icon: (size = 12) => <Bot size={size} />,
    },
    shared_edit_user: {
        color: 'geekblue',
        displayName: 'Shared edit user',
        icon: (size = 12) => <Share2 size={size} />,
    },
    attestations_user: {
        color: 'magenta',
        displayName: 'Attestations user',
        icon: (size = 12) => <ClipboardCheck size={size} />,
    },
    website_checker_user: {
        color: 'lime',
        displayName: 'Website checker user',
        icon: (size = 12) => <Globe size={size} />,
    },
    market_pds_user: {
        color: 'geekblue',
        displayName: 'PDS compare user',
        icon: (size = 12) => <FileText size={size} />,
    },
    training_manager: {
        color: 'gold',
        displayName: 'Training manager',
        icon: (size = 12) => <GraduationCap size={size} />,
    },
    fleet_portal_user: {
        color: 'orange',
        displayName: 'Fleet portal user',
        icon: (size = 12) => <Truck size={size} />,
    },
    fmc_coordinator: {
        color: 'volcano',
        displayName: 'FMC coordinator',
        icon: (size = 12) => <Layers size={size} />,
    },
    compliance_audit_manager: {
        color: 'magenta',
        displayName: 'Compliance audit manager',
        icon: (size = 12) => <ClipboardPen size={size} />,
    },
    compliance_audit_reader: {
        color: 'cyan',
        displayName: 'Compliance audit reader',
        icon: (size = 12) => <ClipboardList size={size} />,
    },
    user: {
        color: 'green',
        displayName: 'User',
        icon: (size = 12) => <User size={size} />,
    },
};

/**
 * Type guard to check if a string is a valid Role
 */
function isValidRole(role: string): role is Role {
    return role in ROLE_CONFIG;
}

/**
 * Get the full role configuration for a role
 */
export function getRoleConfig(role: Role): RoleConfig;
export function getRoleConfig(role: string): RoleConfig | undefined;
export function getRoleConfig(role: string): RoleConfig | undefined {
    if (isValidRole(role)) {
        return ROLE_CONFIG[role];
    }
    return undefined;
}

/**
 * Gets a human-readable display name for a role
 * @param role - The role to get display name for
 * @returns The formatted display name from ROLE_CONFIG
 */
export function getRoleDisplayName(role: Role): string;
export function getRoleDisplayName(role: string): string;
export function getRoleDisplayName(role: string): string {
    if (isValidRole(role)) {
        return ROLE_CONFIG[role].displayName;
    }
    // Fallback for unknown roles
    return role
        .split('_')
        .map((word, index) => {
            const normalisedWord = word.toLowerCase();
            const acronym = ROLE_ACRONYMS.get(normalisedWord);
            if (acronym) return acronym;
            if (index === 0) return normalisedWord.charAt(0).toUpperCase() + normalisedWord.slice(1);
            return normalisedWord;
        })
        .join(' ');
}

/** Builds the form hint shown when required companion roles are locked into a selection. */
export function getRequiredRoleSelectionHint(requiredRoles: readonly OrgRole[]): string | undefined {
    if (requiredRoles.length === 0) return undefined;

    const roleNames = requiredRoles.map(getRoleDisplayName).join(', ');
    const isPlural = requiredRoles.length > 1;
    return `${roleNames} ${isPlural ? 'are' : 'is'} required by your selection, so ${isPlural ? 'they are' : 'it is'} always included.`;
}

/**
 * Get the icon component for a role
 * @param role - The role to get icon for
 * @param size - Icon size in pixels (default: 12)
 * @returns The icon ReactNode or undefined if role not found
 */
export function getRoleIcon(role: Role, size?: number): ReactNode;
export function getRoleIcon(role: string, size?: number): ReactNode | undefined;
export function getRoleIcon(role: string, size?: number): ReactNode | undefined {
    if (isValidRole(role)) {
        return ROLE_CONFIG[role].icon(size);
    }
    return undefined;
}

/**
 * Get the hex colour value for a role's icon
 * @param role - The role to get icon colour for
 * @returns The hex colour string (e.g., '#f5222d')
 */
export function getRoleIconColor(role: Role): string;
export function getRoleIconColor(role: string): string;
export function getRoleIconColor(role: string): string {
    const colorName = getRoleColor(role) as keyof typeof ROLE_COLORS;
    return ROLE_COLORS[colorName] ?? DEFAULT_ROLE_COLOR;
}

/**
 * Icons for admin console roles (used in role selector UI)
 */
export const ADMIN_ROLE_ICONS: Record<AdminRole, ReactNode> = {
    [ADMIN_ROLES.SUPER_ADMIN]: <Crown className="h-5 w-5" />,
    [ADMIN_ROLES.ADMIN_READ_ONLY]: <Eye className="h-5 w-5" />,
    [ADMIN_ROLES.ADMIN_ORG_MANAGER]: <Building2 className="h-5 w-5" />,
    [ADMIN_ROLES.ADMIN_USER_MANAGER]: <Users className="h-5 w-5" />,
    [ADMIN_ROLES.ADMIN_CONTENT_MANAGER]: <FileText className="h-5 w-5" />,
    [ADMIN_ROLES.ADMIN_COMMS_MANAGER]: <Send className="h-5 w-5" />,
    [ADMIN_ROLES.ADMIN_AI_MANAGER]: <Bot className="h-5 w-5" />,
    [ADMIN_ROLES.ADMIN_SYSTEM_OPS]: <Server className="h-5 w-5" />,
    [ADMIN_ROLES.ADMIN_OPS_READER]: <Eye className="h-5 w-5" />,
    [ADMIN_ROLES.ADMIN_SUPPORT_MANAGER]: <Headphones className="h-5 w-5" />,
    [ADMIN_ROLES.ADMIN_SUPPORT_USER]: <Headphones className="h-5 w-5" />,
};

/**
 * Ordered list of admin roles for display in UI
 */
export const ADMIN_ROLE_ORDER: AdminRole[] = [
    ADMIN_ROLES.SUPER_ADMIN,
    ADMIN_ROLES.ADMIN_READ_ONLY,
    ADMIN_ROLES.ADMIN_ORG_MANAGER,
    ADMIN_ROLES.ADMIN_USER_MANAGER,
    ADMIN_ROLES.ADMIN_CONTENT_MANAGER,
    ADMIN_ROLES.ADMIN_COMMS_MANAGER,
    ADMIN_ROLES.ADMIN_AI_MANAGER,
    ADMIN_ROLES.ADMIN_SYSTEM_OPS,
    ADMIN_ROLES.ADMIN_OPS_READER,
    ADMIN_ROLES.ADMIN_SUPPORT_MANAGER,
    ADMIN_ROLES.ADMIN_SUPPORT_USER,
];

/**
 * Ordered list of organisation roles for display in UI
 */
export const ORG_ROLE_ORDER: OrgRole[] = [
    ORG_ROLES.ORG_ADMIN,
    ORG_ROLES.USER_MANAGER,
    ORG_ROLES.USER,
    ORG_ROLES.SUPPLIER,
    ORG_ROLES.SHARED_EDIT_USER,
    ORG_ROLES.TRAINING_MANAGER,
    ORG_ROLES.AUTO_DETECT_USER,
    ORG_ROLES.WEBSITE_CHECKER_USER,
    ORG_ROLES.MARKET_PDS_USER,
    ORG_ROLES.ATTESTATIONS_USER,
    ORG_ROLES.FLEET_PORTAL_USER,
    ORG_ROLES.FMC_COORDINATOR,
    ORG_ROLES.COMPLIANCE_AUDIT_MANAGER,
    ORG_ROLES.COMPLIANCE_AUDIT_READER,
];

/** A titled section of roles in a role picker, in display order. */
export interface RoleGroup<R extends Role> {
    id: string;
    title: string;
    roles: R[];
}

const ORG_ROLE_GROUP_TITLES = {
    console: 'Console access',
    modules: 'Module access',
    portals: 'External portals',
} as const satisfies Record<OrgRoleGroup, string>;

const ADMIN_ROLE_GROUP_TITLES = {
    platform: 'Platform-wide',
    management: 'Management',
    operations: 'Operations and support',
} as const satisfies Record<AdminRoleGroup, string>;

/** Sort organisation roles into their picker sections, keeping the given order and dropping empty sections. */
export function groupOrgRoles(roles: readonly OrgRole[]): RoleGroup<OrgRole>[] {
    return (Object.keys(ORG_ROLE_GROUP_TITLES) as OrgRoleGroup[])
        .map((id) => ({
            id,
            title: ORG_ROLE_GROUP_TITLES[id],
            roles: roles.filter((role) => ROLE_METADATA[role].group === id),
        }))
        .filter((group) => group.roles.length > 0);
}

/** Admin console roles sorted into their picker sections. */
export const ADMIN_ROLE_GROUPS: RoleGroup<AdminRole>[] = (Object.keys(ADMIN_ROLE_GROUP_TITLES) as AdminRoleGroup[]).map(
    (id) => ({
        id,
        title: ADMIN_ROLE_GROUP_TITLES[id],
        roles: ADMIN_ROLE_ORDER.filter((role) => ADMIN_ROLE_METADATA[role].group === id),
    }),
);

/**
 * Icons for organisation roles (used in role selector UI)
 */
export const ORG_ROLE_ICONS: Record<OrgRole, ReactNode> = {
    [ORG_ROLES.ORG_ADMIN]: <Shield className="h-5 w-5" />,
    [ORG_ROLES.USER_MANAGER]: <Users className="h-5 w-5" />,
    [ORG_ROLES.USER]: <User className="h-5 w-5" />,
    [ORG_ROLES.SUPPLIER]: <Building2 className="h-5 w-5" />,
    [ORG_ROLES.AUTO_DETECT_USER]: <Bot className="h-5 w-5" />,
    [ORG_ROLES.SHARED_EDIT_USER]: <Share2 className="h-5 w-5" />,
    [ORG_ROLES.TRAINING_MANAGER]: <GraduationCap className="h-5 w-5" />,
    [ORG_ROLES.ATTESTATIONS_USER]: <ClipboardCheck className="h-5 w-5" />,
    [ORG_ROLES.WEBSITE_CHECKER_USER]: <Globe className="h-5 w-5" />,
    [ORG_ROLES.MARKET_PDS_USER]: <FileText className="h-5 w-5" />,
    [ORG_ROLES.FLEET_PORTAL_USER]: <Truck className="h-5 w-5" />,
    [ORG_ROLES.FMC_COORDINATOR]: <Layers className="h-5 w-5" />,
    [ORG_ROLES.COMPLIANCE_AUDIT_MANAGER]: <ClipboardPen className="h-5 w-5" />,
    [ORG_ROLES.COMPLIANCE_AUDIT_READER]: <ClipboardList className="h-5 w-5" />,
};

// Re-export getRoleColor from role-colors
export { getRoleColor };
